import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Pause, SealCheck, FolderSimple, CaretRight, ArrowLeft } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";

const CATS = [["doa", "Doa"], ["hadits", "Hadits"], ["arab", "B. Arab"], ["inggris", "B. Inggris"], ["lainnya", "Lainnya"]];

export default function Materi() {
  const [sp] = useSearchParams();
  const wantTab = sp.get("tab");
  const wantItem = sp.get("item");
  const [tab, setTab] = useState(CATS.some((c) => c[0] === wantTab) ? wantTab : "doa");
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [path, setPath] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const audioRef = useRef(null);

  const stop = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } setPlaying(null); };
  useEffect(() => {
    api.get(`/content/${tab}`).then((r) => setItems(r.data));
    api.get(`/content-folders/${tab}`).then((r) => setFolders(r.data));
    setPath([]); stop();
  }, [tab]);
  useEffect(() => () => stop(), []);
  useEffect(() => {
    if (!wantItem || !items.length) return;
    const match = items.find((it) => String(it.id) === wantItem || String(it.key) === wantItem || (it.label && it.label === wantItem));
    if (match) {
      setHighlightId(match.id);
      setTimeout(() => { const el = document.querySelector(`[data-testid="materi-item-${match.id}"]`); if (el) el.scrollIntoView({ behavior: "smooth", block: "center" }); }, 350);
      const t = setTimeout(() => setHighlightId(null), 2600);
      return () => clearTimeout(t);
    }
  }, [items, wantItem]);

  const toggle = (it) => {
    if (playing === it.id) { stop(); return; }
    stop();
    const a = new Audio(it.audio); a.onended = () => setPlaying(null); a.play().catch(() => {});
    audioRef.current = a; setPlaying(it.id);
  };

  const title = (it) => it.label || it.arabic || it.latin || "Item";
  const folder = path[path.length - 1] || null;
  const currentFid = folder?.id || null;
  const subFolders = folders.filter((f) => (f.parent_id || null) === currentFid);
  const visibleItems = items.filter((it) => (it.folder_id || null) === currentFid);

  return (
    <div>
      <PageHeader title="Materi Hafalan" />
      <div className="px-5 pt-4">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-4">
          {CATS.map(([v, l]) => (
            <button key={v} data-testid={`materi-tab-${v}`} onClick={() => setTab(v)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === v ? "bg-primary text-white shadow" : "bg-white border border-stone-200 text-stone-500"}`}>{l}</button>
          ))}
        </div>

        {path.length > 0 && (
          <div className="flex items-center gap-1 mb-3 text-sm flex-wrap" data-testid="materi-breadcrumb">
            <button onClick={() => setPath([])} className="text-primary font-semibold">Semua</button>
            {path.map((f, i) => (
              <span key={f.id} className="flex items-center gap-1">
                <CaretRight size={12} className="text-stone-300" />
                <button data-testid="materi-folder-back" onClick={() => setPath(path.slice(0, i + 1))} className={`font-semibold ${i === path.length - 1 ? "text-stone-800" : "text-primary"}`}>{f.title}</button>
              </span>
            ))}
          </div>
        )}

        {subFolders.length > 0 && (
          <div className="space-y-2 mb-4" data-testid="materi-folders">
            {subFolders.map((f) => (
              <button key={f.id} data-testid={`materi-folder-${f.id}`} onClick={() => setPath([...path, f])}
                className="w-full bg-white rounded-2xl border border-stone-200/60 p-3.5 flex items-center gap-3 shadow-sm active:scale-[0.98] transition-transform">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0"><FolderSimple size={20} weight="fill" className="text-amber-500" /></div>
                <span className="font-semibold text-stone-800 text-sm truncate flex-1 text-left">{f.title}</span>
                <CaretRight size={16} className="text-stone-300 shrink-0" />
              </button>
            ))}
          </div>
        )}

        <div className="space-y-4 pb-6">
          {visibleItems.map((it, i) => (
            <motion.div key={it.id} data-testid={`materi-item-${it.id}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className={`bg-white rounded-3xl border p-5 shadow-sm transition-all ${highlightId === it.id ? "border-primary ring-2 ring-primary/40" : "border-stone-200/60"}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-heading font-bold text-primary flex-1">{title(it)}</h3>
                {it.audio ? (
                  <button data-testid={`materi-play-${it.id}`} onClick={() => toggle(it)}
                    className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center shrink-0 active:scale-90 transition-transform shadow">
                    {playing === it.id ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
                  </button>
                ) : (
                  <button disabled data-testid={`materi-play-disabled-${it.id}`}
                    className="w-10 h-10 rounded-full bg-stone-100 text-stone-300 flex items-center justify-center shrink-0" aria-label="Belum ada audio">
                    <Play size={18} weight="fill" />
                  </button>
                )}
              </div>
              {it.arabic && <p className="font-arabic text-3xl leading-loose text-right text-stone-800 mb-2">{it.arabic}</p>}
              {it.latin && <p className="text-primary/80 italic text-sm mb-1">{it.latin}</p>}
              {it.meaning && <p className="text-stone-700 text-sm">{it.meaning}</p>}
              {it.translation && <p className="text-stone-600 text-sm leading-relaxed border-t border-stone-100 pt-2 mt-1">{it.translation}</p>}
              {it.example && <p className="text-stone-500 text-sm italic mt-1">"{it.example}"</p>}
              {it.description && <p className="text-stone-600 text-sm mt-1">{it.description}</p>}
              {it.source && (
                <p className="text-[11px] font-semibold text-primary/80 mt-2.5 inline-flex items-center gap-1 bg-primary/10 px-2.5 py-1 rounded-full">
                  <SealCheck size={13} weight="fill" /> {it.source}
                </p>
              )}
            </motion.div>
          ))}
          {visibleItems.length === 0 && subFolders.length === 0 && <p className="text-center text-stone-400 mt-10">Belum ada materi pada folder ini.</p>}
        </div>
      </div>
    </div>
  );
}
